import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy, HelpCircle, Layers } from "lucide-react";

/**
 * Pattern Match (Set) Game
 * 
 * Rules:
 * Find sets of 3 cards where for each attribute (Shape, Color, Number, Fill),
 * they are either all the same OR all different.
 */

// --- Constants ---
const SHAPES = ["diamond", "oval", "squiggle"];
const COLORS = ["red", "purple", "green"]; // utilizing standard CSS colors or specific hex
const FILLS = ["solid", "striped", "open"];
const NUMBERS = [1, 2, 3];

// Color Map
const COLOR_MAP = {
    red: "#ef4444", // red-500
    purple: "#a855f7", // purple-500
    green: "#22c55e", // green-500
};

const PatternMatchGame = () => {
    const navigate = useNavigate();
    
    // --- State ---
    const [deck, setDeck] = useState([]);
    const [board, setBoard] = useState([]);
    const [selected, setSelected] = useState([]); // indices of selected cards on board
    const [score, setScore] = useState(0);
    const [message, setMessage] = useState("");
    const [showRules, setShowRules] = useState(false);
    const [gameWon, setGameWon] = useState(false);

    // --- Init ---
    useEffect(() => {
        startNewGame();
    }, []);

    const startNewGame = () => {
        const newDeck = [];
        // Generate all 81 cards
        for (let s of SHAPES) {
            for (let c of COLORS) {
                for (let f of FILLS) {
                    for (let n of NUMBERS) {
                        newDeck.push({ shape: s, color: c, fill: f, number: n, id: Math.random() });
                    }
                }
            }
        }
        // Shuffle
        for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
        }
        
        // Deal 12
        const initialBoard = newDeck.slice(0, 12);
        const remainingDeck = newDeck.slice(12);

        setDeck(remainingDeck);
        setBoard(initialBoard);
        setSelected([]);
        setScore(0);
        setMessage("");
        setGameWon(false);
    };

    // --- Logic ---
    const isSet = (c1, c2, c3) => {
        if (!c1 || !c2 || !c3) return false;
        
        const attrs = ["shape", "color", "fill", "number"];
        for (let attr of attrs) {
            const v1 = c1[attr];
            const v2 = c2[attr];
            const v3 = c3[attr];
            const allSame = (v1 === v2 && v2 === v3);
            const allDiff = (v1 !== v2 && v2 !== v3 && v1 !== v3);
            if (!allSame && !allDiff) return false;
        }
        return true;
    };

    const handleCardClick = (cardIndex) => {
        // Toggle selection
        let newSelected = [...selected];
        if (newSelected.includes(cardIndex)) {
            newSelected = newSelected.filter(i => i !== cardIndex);
        } else {
            if (newSelected.length < 3) {
                newSelected.push(cardIndex);
            }
        }
        setSelected(newSelected);

        // Check if 3 selected
        if (newSelected.length === 3) {
            const c1 = board[newSelected[0]];
            const c2 = board[newSelected[1]];
            const c3 = board[newSelected[2]];

            if (isSet(c1, c2, c3)) {
                setMessage("Match found! +1 Point");
                setScore(s => s + 1);
                setTimeout(() => replaceCards(newSelected), 500); // Delay for visual feedback
            } else {
                setMessage("Not a match. Try again.");
                setTimeout(() => setSelected([]), 800);
            }
        } else {
            setMessage("");
        }
    };

    const replaceCards = (indicesToRemove) => {
        // Remove cards and replace from deck if available
        // Need to maintain board order to not confuse user, or just fill gaps?
        // Standard Set: If 12 cards, replace. If >12 (due to add 3), just remove.
        
        const newBoard = [...board];
        let currentDeck = [...deck];
        
        // Sort indices descending to remove cleanly if we were splicing, 
        // but we are replacing slots.
        
        const needReplacement = newBoard.length <= 12 && currentDeck.length > 0;
        
        if (newBoard.length > 12 || currentDeck.length === 0) {
            // Just remove them (filter out), unless we drop below 12 and have cards?
            // Actually, simplest logic: Mark as null then compact? 
            // Or just filter out. If we filter out, layout shifts. 
            // Better to standard Set rules: 
            // If < 12 cards on board, or if we have > 12 cards, handle gracefully.
            
            // Implementation:
            // If board > 12, just remove the 3 cards.
            // If board <= 12, replace the 3 cards with 3 from deck.
            // If deck empty, just remove (and board shrinks).
            
            if (newBoard.length > 12) {
                 // Remove functionality
                 // We need to map indices to card objects first to filter correctly or use ID
                 const cardsToRemove = indicesToRemove.map(i => newBoard[i].id);
                 const filteredBoard = newBoard.filter(c => !cardsToRemove.includes(c.id));
                 setBoard(filteredBoard);
            } else {
                // Try to replace
                indicesToRemove.forEach(idx => {
                    if (currentDeck.length > 0) {
                        newBoard[idx] = currentDeck.pop();
                    } else {
                        newBoard[idx] = null; // Mark for removal if deck empty
                    }
                });
                 // Clean up nulls
                const cleanedBoard = newBoard.filter(c => c !== null);
                setBoard(cleanedBoard);
                setDeck(currentDeck);
            }
        } else {
             // Standard replace
            indicesToRemove.forEach(idx => {
                if (currentDeck.length > 0) {
                    newBoard[idx] = currentDeck.pop();
                } else {
                    newBoard[idx] = null;
                }
            });
            const cleanedBoard = newBoard.filter(c => c !== null);
            setBoard(cleanedBoard);
            setDeck(currentDeck);
        }
        
        setSelected([]);
        
        // Final check
        if (board.length === 0 && deck.length === 0) setGameWon(true);
    };

    const addThreeCards = () => {
        if (deck.length === 0) {
            setMessage("No more cards in deck!");
            return;
        }
        const currentDeck = [...deck];
        const newCards = currentDeck.splice(0, 3);
        setBoard([...board, ...newCards]);
        setDeck(currentDeck);
    };

    // --- Render Helpers ---
    const renderShape = (card) => {
        const color = COLOR_MAP[card.color];
        
        // SVG Definitions for Fill
        // Unique ID for pattern to avoid conflicts if multiple games/cards
        const patternId = `stripe-${card.color}`; 
        
        // For striped, we use the pattern. For solid, fill=color. For open, fill="transparent".
        let fillValue;
        if (card.fill === "solid") fillValue = color;
        else if (card.fill === "open") fillValue = "none";
        else fillValue = `url(#${patternId})`;

        const shapes = [];
        for (let i = 0; i < card.number; i++) {
            shapes.push(
                <svg key={i} width="40" height="80" viewBox="0 0 50 100" className="inline-block mx-0.5">
                    <defs>
                        <pattern id={patternId} width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                            <line x1="0" y1="0" x2="0" y2="10" stroke={color} strokeWidth="2" />
                        </pattern>
                    </defs>
                    {card.shape === "diamond" && (
                         <polygon points="25,5 45,50 25,95 5,50" stroke={color} strokeWidth="3" fill={fillValue} />
                    )}
                    {card.shape === "oval" && (
                         <rect x="5" y="15" width="40" height="70" rx="20" ry="20" stroke={color} strokeWidth="3" fill={fillValue} />
                    )}
                    {card.shape === "squiggle" && (
                         // Approximate squiggle path
                         <path d="M10,15 C5,15 5,30 15,35 C35,45 35,55 15,65 C5,70 5,85 15,85 C25,85 40,85 40,85 C45,85 45,70 35,65 C15,55 15,45 35,35 C45,30 45,15 35,15 Z" stroke={color} strokeWidth="3" fill={fillValue} />
                    )}
                </svg>
            );
        }
        return <div className="flex items-center justify-center">{shapes}</div>;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            <div className="w-full max-w-4xl px-4 mb-4 flex items-center justify-between">
                <button 
                    onClick={() => navigate('/games')} 
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                    <ArrowLeft className="h-5 w-5 mr-1" /> 
                    Back to Games
                </button>
                <div className="flex gap-4 items-center">
                    <div className="bg-white px-4 py-2 rounded-lg shadow-sm font-bold text-gray-700">
                        Score: {score}
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg shadow-sm font-bold text-indigo-600">
                         Deck: {deck.length}
                    </div>
                </div>
                <div className="flex gap-2">
                     <button
                        onClick={() => setShowRules(!showRules)}
                        className={`p-2 rounded transition-colors ${showRules ? "text-blue-600 bg-blue-50" : "text-gray-500 hover:text-blue-600"}`}
                        title="Rules"
                    >
                        <HelpCircle className="h-5 w-5" />
                    </button>
                    <button onClick={startNewGame} className="p-2 text-gray-500 hover:text-blue-600" title="Restart">
                         <RefreshCw className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Rules */}
            {showRules && (
                 <div className="max-w-2xl w-full bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-blue-900 animate-fade-in shadow-sm">
                    <h3 className="font-bold text-lg mb-3">How to Play Pattern Match</h3>
                    <p className="mb-2 text-sm">Find sets of <strong>3 cards</strong>. A valid set must satisfy the following rule for <strong>EACH</strong> of the 4 features (Shape, Color, Number, Shading):</p>
                    <p className="mb-4 font-medium text-center bg-white/50 p-2 rounded">"They are all the SAME, or they are all DIFFERENT."</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Example 1: All Red, All Ovals, All Solid, Numbers 1, 2, 3. (Valid: Color/Shape/Shade same, Numbers diff)</li>
                        <li>Example 2: Red Diamond Solid 1, Green Oval Striped 2, Purple Squiggle Open 3. (Valid: Everything different)</li>
                    </ul>
                 </div>
            )}

            {/* Feedback */}
            <div className={`h-8 mb-4 font-bold text-lg transition-colors ${message.includes("found") ? "text-green-600" : "text-red-500"}`}>
                {message}
            </div>

            {/* Board */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 gap-4 p-4 max-w-5xl">
                {board.map((card, idx) => (
                    <button
                        key={card.id}
                        onClick={() => handleCardClick(idx)}
                        className={`
                            relative bg-white rounded-xl shadow-md border-2 p-4 h-32 w-24 sm:h-40 sm:w-32 flex items-center justify-center transition-all transform hover:scale-105
                            ${selected.includes(idx) ? "border-blue-500 ring-2 ring-blue-300 scale-105" : "border-gray-200"}
                        `}
                    >
                        {renderShape(card)}
                    </button>
                ))}
            </div>
            
            <div className="mt-8 flex gap-4">
                 <button 
                    onClick={addThreeCards}
                    disabled={deck.length === 0}
                    className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-md font-medium"
                 >
                     <Layers className="h-5 w-5 mr-2" />
                     Draw 3 More Cards
                 </button>
            </div>
        </div>
    );
};

export default PatternMatchGame;
